import { useEffect, useRef, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { useLocation } from "react-router";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ref, push, set, update } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { database, storage } from "../constants/firebase";
import MobileHeader from "../components/MobileHeader";
import { convertToWebP, uploadFileToBunny } from "../utils/utils";
import { FiMinus } from "react-icons/fi";

const AddLesson = () => {
    const navigate = useMoveTo();
    const location = useLocation();
    const { currentUser } = useUser();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState({
        title: "",
        coverImage: null,
        description: "",
        status: "Draft",
        lessonFile: null, // PDF or Video
        courseLinks: [""],
    });
    const coverInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const [isCoverImage, setIsCoverImage] = useState(false);
    const [existingFile, setExistingFile] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);


    useEffect(() => {
        if (location.state?.lesson) {
            setForm({
                title: location.state.lesson.title,
                coverImage: null,
                description: location.state.lesson.description || "",
                status: location.state.lesson.status || "Draft",
                lessonFile: null,
                courseLinks: location.state.lesson.courseLinks?.length
                    ? location.state.lesson.courseLinks
                    : [""],
            });
            setIsCoverImage(!!location.state.lesson.coverImageUrl);
            setExistingFile(location.state.lesson.fileUrl || "");
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({
            ...form,
            [name]: value,
        });
    };

    const handleCoverUpload = (e) => {
        setForm({ ...form, coverImage: e.target.files[0] });
    };

    const handleFileUpload = (e) => {
        setForm({ ...form, lessonFile: e.target.files[0] });
    };

    const triggerCoverUpload = () => {
        coverInputRef.current?.click();
    };

    const triggerFileUpload = () => {
        fileInputRef.current?.click();
    };

    const handleSave = async () => {
        if (!form.title || (!form.coverImage && !isCoverImage)) {
            window.showToast("Please fill all required fields", "error");
            return;
        }

        const hasAtLeastOneLink = form.courseLinks.some(link => link.trim() !== "");

        if (!form.lessonFile && !existingFile && !hasAtLeastOneLink) {
            window.showToast(
                "Please upload a lesson video OR add at least one lesson link",
                "error"
            );
            return;
        }

        setIsSubmitting(true);

        try {
            let coverImageUrl = null;
            let fileUrl = existingFile || "";
            let videoOrientation = location.state?.lesson?.videoOrientation || null;
            let videoWidth = location.state?.lesson?.videoWidth || null;
            let videoHeight = location.state?.lesson?.videoHeight || null;

            // -----------------------------------------------------
            // 🖼 1️⃣ Upload Cover Image (WebP conversion)
            // -----------------------------------------------------
            if (form.coverImage) {
                const webpFile = await convertToWebP(form.coverImage);
                const storageReference = storageRef(
                    storage,
                    `lesson_covers/${Date.now()}_${webpFile.name}`
                );
                await uploadBytes(storageReference, webpFile);
                coverImageUrl = await getDownloadURL(storageReference);
            }

            const courseId = location.state?.courseId;
            const chapterId = location.state?.chapterId;

            if (!courseId || !chapterId) {
                window.showToast("Course or Chapter not found!", "error");
                return;
            }

            // -----------------------------------------------------
            // ✏️ 2️⃣ UPDATE EXISTING LESSON
            // -----------------------------------------------------
            if (location.state?.lesson?.id) {
                const lessonId = location.state.lesson.id;

                // Upload lesson video/PDF if replaced
                if (form.lessonFile) {
                    const isVideo = form.lessonFile.type.startsWith("video/");

                    if (isVideo) {
                        const bunnyResult = await uploadFileToBunny(
                            form.lessonFile,
                            `lessons_${lessonId}`, // FIXED
                            form.title || "Lesson Video",
                            (percent) => setUploadProgress(percent)
                        );

                        if (bunnyResult.success) {
                            fileUrl = bunnyResult.playbackUrl;
                            videoOrientation = bunnyResult.videoOrientation;
                            videoWidth = bunnyResult.videoWidth;
                            videoHeight = bunnyResult.videoHeight;
                        } else {
                            throw new Error("Bunny upload failed: " + bunnyResult.message);
                        }
                    } else {
                        const storageReference = storageRef(
                            storage,
                            `lesson_files/${Date.now()}_${form.lessonFile.name}`
                        );
                        await uploadBytes(storageReference, form.lessonFile);
                        fileUrl = await getDownloadURL(storageReference);
                        videoOrientation = null;
                        videoWidth = null;
                        videoHeight = null;
                    }
                }

                const updates = {
                    title: form.title,
                    description: form.description,
                    status: form.status,
                    courseLinks: form.courseLinks.filter(link => link.trim() !== ""),
                    ...(coverImageUrl && { coverImageUrl }),
                    ...(fileUrl && { fileUrl }),
                    ...(form.lessonFile && { videoOrientation, videoWidth, videoHeight }),
                };

                await update(
                    ref(database, `Listings/${courseId}/chapters/${chapterId}/lessons/${lessonId}`),
                    updates
                );

                window.showToast("Lesson updated successfully!", "success");
                navigate(-1);
                return;
            }

            // -----------------------------------------------------
            // 🆕 3️⃣ CREATE NEW LESSON STEP 1: Create Placeholder First
            // -----------------------------------------------------
            const newLessonRef = push(
                ref(database, `Listings/${courseId}/chapters/${chapterId}/lessons`)
            );
            const lessonId = newLessonRef.key;

            // -----------------------------------------------------
            // 🎥 4️⃣ Upload Lesson Video using lessonId
            // -----------------------------------------------------
            if (form.lessonFile) {
                const isVideo = form.lessonFile.type.startsWith("video/");

                if (isVideo) {
                    const bunnyResult = await uploadFileToBunny(
                        form.lessonFile,
                        `lessons_${lessonId}`, // FIXED - per lesson
                        form.title || "Lesson Video",
                        (percent) => setUploadProgress(percent)
                    );

                    if (bunnyResult.success) {
                        fileUrl = bunnyResult.playbackUrl;
                        videoOrientation = bunnyResult.videoOrientation;
                        videoWidth = bunnyResult.videoWidth;
                        videoHeight = bunnyResult.videoHeight;
                    } else {
                        throw new Error("Bunny upload failed: " + bunnyResult.message);
                    }
                } else {
                    const storageReference = storageRef(
                        storage,
                        `lesson_files/${Date.now()}_${form.lessonFile.name}`
                    );
                    await uploadBytes(storageReference, form.lessonFile);
                    fileUrl = await getDownloadURL(storageReference);
                    videoOrientation = null;
                    videoWidth = null;
                    videoHeight = null;
                }
            }

            // -----------------------------------------------------
            // 💾 5️⃣ Save New Lesson
            // -----------------------------------------------------
            await set(newLessonRef, {
                id: lessonId,
                title: form.title,
                description: form.description,
                status: form.status,
                coverImageUrl: coverImageUrl || "",
                fileUrl: fileUrl || "",
                videoOrientation,
                videoWidth,
                videoHeight,
                courseLinks: form.courseLinks.filter(link => link.trim() !== ""),
                createdAt: Date.now(),
            });

            window.showToast("Lesson saved successfully!", "success");

            // Reset
            setForm({
                title: "",
                description: "",
                coverImage: null,
                lessonFile: null,
                status: "Draft",
            });

        } catch (error) {
            console.error("❌ Error saving lesson:", error);
            window.showToast("Failed to save lesson.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLinkChange = (index, value) => {
        const updatedLinks = [...form.courseLinks];
        updatedLinks[index] = value;
        setForm({ ...form, courseLinks: updatedLinks });
    };

    const addLinkField = () => {
        setForm({ ...form, courseLinks: [...form.courseLinks, ""] });
    };

    const removeLinkField = (index) => {
        const updatedLinks = form.courseLinks.filter((_, i) => i !== index);
        setForm({ ...form, courseLinks: updatedLinks.length ? updatedLinks : [""] });
    };




    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader title={location.state?.lesson ? "Edit Lesson" : "Add Lesson"} isBackBtn={true} />
            <div className="flex">
                <Sidebar />
                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img
                                src={ICONS.arrowBack}
                                className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                                alt="Back"
                            />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                            {location.state?.lesson ? "Edit Lesson" : "Add Lesson"}
                        </h2>
                        <div />
                    </div>

                    <div className="max-w-md mx-auto md:pt-10 pt-3 space-y-6">
                        {/* Lesson Title */}
                        <input
                            type="text"
                            name="title"
                            placeholder="Lesson Title"
                            value={form.title}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] text-black border border-gray-200"
                        />

                        {/* Cover Upload */}
                        <div
                            className="w-full cursor-pointer border-dashed border-2 border-gray-400 p-6 text-center rounded-xl bg-[#F5F9FE] text-[#7C8BA0]"
                            onClick={triggerCoverUpload}
                        >
                            <input
                                ref={coverInputRef}
                                type="file"
                                accept="image/png,image/jpg,image/jpeg"
                                className="hidden"
                                onChange={handleCoverUpload}
                            />
                            <img src={ICONS.upload} className="mx-auto mb-2 w-8 h-8" alt="Upload" />
                            <p className="font-semibold text-base text-[#7C8BA0]">Upload Lesson Cover Image:</p>
                            <p className="text-xs text-[#7C8BA0]">Use 16:9 for best results; other images will be auto-cropped (.png, .jpg, .jpeg).</p>

                            {form.coverImage && <p className="mt-2 text-xs text-green-600">Selected: {form.coverImage.name}</p>}
                            {!form.coverImage && isCoverImage && <p className="mt-2 text-xs text-green-600">Cover image already exists</p>}
                        </div>

                        {/* File Upload */}
                        <div
                            className="w-full cursor-pointer border-dashed border-2 border-gray-400 p-6 text-center rounded-xl bg-[#F5F9FE] text-[#7C8BA0]"
                            onClick={triggerFileUpload}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="video/mp4,video/quicktime"   // ✅ only mp4 and mov
                                className="hidden"
                                onChange={handleFileUpload}
                            />
                            <img src={ICONS.upload} className="mx-auto mb-2 w-8 h-8" alt="Upload" />
                            <p className="font-semibold text-base text-[#7C8BA0]">Upload Lesson Video:</p>
                            <p className="text-xs text-[#7C8BA0]">
                                Use 16:9 and max 15 minutes for best results; other videos will be auto-cropped (mp4, mov).
                            </p>

                            {form.lessonFile && (
                                <p className="mt-2 text-xs text-green-600">
                                    Selected: {form.lessonFile.name}
                                </p>
                            )}

                            {!form.lessonFile && existingFile && (
                                <p className="mt-2 text-xs text-green-600">
                                    Video already uploaded
                                </p>
                            )}
                        </div>

                        {/* Lesson Link */}
                        <div className="space-y-3">
                            {form.courseLinks.map((link, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder={`Lesson Link ${index + 1}`}
                                        value={link}
                                        onChange={(e) => handleLinkChange(index, e.target.value)}
                                        className="flex-1 rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] text-black border border-gray-200"
                                    />

                                    {/* ❌ No remove button for first link */}
                                    {index > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => removeLinkField(index)}
                                            className="text-gray-400 hover:text-red-500"
                                        >
                                            <FiMinus />
                                        </button>
                                    )}
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={addLinkField}
                                className="text-sm font-semibold text-[#327691]"
                            >
                                + Add another link
                            </button>
                        </div>


                        {/* Lesson Description */}
                        <input
                            type="text"
                            name="description"
                            placeholder="Lesson Description"
                            value={form.description}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] text-black border border-gray-200"
                        />

                        {/* Status */}
                        <select
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWNoZXZyb24tZG93biI+PHBhdGggZD0ibTYgOSA2IDYgNi02Ii8+PC9zdmc+')] bg-no-repeat bg-[position:right_0.5rem_center] bg-[size:1.5em_1.5em]"
                        >
                            <option value="Draft">Draft</option>
                            <option value="Published">Published</option>
                        </select>

                        {/* Save Button */}
                        <button
                            onClick={handleSave}
                            className="bg-[#327691] text-white text-lg font-semibold py-3 rounded-xl w-full mt-4"
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? uploadProgress > 0
                                    ? `Uploading Video… ${uploadProgress}%`
                                    : "Saving..."
                                : "Save Lesson"}
                        </button>
                    </div>
                </main>
            </div >
        </div >
    );
};

export default AddLesson;
